<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Programmes</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Programme
        </button>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-3">
              <b-form-input
                v-model="filter"
                placeholder="Search programmes..."
                debounce="300"
              ></b-form-input>
            </div>
            <div class="col-md-3">
              <b-form-select v-model="collegeFilter" @change="onCollegeFilterChange">
                <option value="">All Colleges</option>
                <option v-for="c in colleges" :key="c.id" :value="c.id">{{ c.college }}</option>
              </b-form-select>
            </div>
            <div class="col-md-3">
              <b-form-select v-model="departmentFilter" @change="loadProgrammes">
                <option value="">All Departments</option>
                <option v-for="d in filteredDepartments" :key="d.id" :value="d.id">{{ d.department }}</option>
              </b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="degreeFilter" @change="currentPage = 1">
                <option value="">All Degrees</option>
                <option v-for="deg in degreeOptions" :key="deg" :value="deg">{{ deg }}</option>
              </b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="progTypeFilter" @change="currentPage = 1">
                <option value="">All Types</option>
                <option value="FT">Full Time (FT)</option>
                <option value="PT">Part Time (PT)</option>
              </b-form-select>
            </div>
          </div>

          <!-- Bulk Action Bar -->
          <div class="alert alert-warning d-flex justify-content-between align-items-center mb-3" v-if="selectedIds.length > 0">
            <span><strong>{{ selectedIds.length }}</strong> programme(s) selected</span>
            <div>
              <button class="btn btn-sm btn-outline-secondary mr-2" @click="selectedIds = []">Clear</button>
              <button class="btn btn-sm btn-primary" @click="openBulkUpdateModal">
                <b-icon icon="pencil-square" class="mr-1"></b-icon> Bulk Update
              </button>
            </div>
          </div>

          <b-table
            :items="filteredProgrammes"
            :fields="tableFields"
            :filter="filter"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #head(selected)>
              <b-form-checkbox :checked="allSelected" @change="toggleSelectAll"></b-form-checkbox>
            </template>
            <template #cell(selected)="data">
              <b-form-checkbox :checked="selectedIds.includes(data.item.id)" @change="toggleSelect(data.item.id)"></b-form-checkbox>
            </template>
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading programmes...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No programmes found.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(department_name)="data">
              {{ data.item.department ? data.item.department.department : "N/A" }}
            </template>
            <template #cell(college_name)="data">
              {{ data.item.department && data.item.department.college ? data.item.department.college.college : "N/A" }}
            </template>
            <template #cell(prog_status)="data">
              <b-badge :variant="data.value == 1 || data.value === 'active' ? 'success' : 'secondary'">
                {{ data.value == 1 || data.value === 'active' ? 'Active' : data.value == 0 || data.value === 'inactive' ? 'Inactive' : 'N/A' }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="filteredProgrammes.length > perPage">
            <small class="text-muted">Showing {{ filteredProgrammes.length }} programmes</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredProgrammes.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Programme' : 'Add Programme'"
        @ok.prevent="saveProgramme"
        :ok-disabled="saving"
        size="lg"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveProgramme">
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Programme Name" label-for="prog-name">
                <b-form-input
                  id="prog-name"
                  v-model="form.programme"
                  placeholder="e.g. Computer Science"
                  :disabled="saving"
                  :state="formErrors.programme ? false : null"
                ></b-form-input>
                <b-form-invalid-feedback>{{ formErrors.programme }}</b-form-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Programme Code" label-for="prog-code">
                <b-form-input
                  id="prog-code"
                  v-model="form.code"
                  placeholder="e.g. CMP*MSc*FT"
                  :disabled="saving"
                  :state="formErrors.code ? false : null"
                ></b-form-input>
                <b-form-invalid-feedback>{{ formErrors.code }}</b-form-invalid-feedback>
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Department" label-for="prog-dept">
                <b-form-select
                  id="prog-dept"
                  v-model="form.department_id"
                  :disabled="saving"
                  :state="formErrors.department_id ? false : null"
                >
                  <option :value="null" disabled>-- Select Department --</option>
                  <option v-for="d in allDepartments" :key="d.id" :value="d.id">
                    {{ d.department }} ({{ d.college ? d.college.college : '' }})
                  </option>
                </b-form-select>
                <b-form-invalid-feedback>{{ formErrors.department_id }}</b-form-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Programme ID" label-for="prog-id">
                <b-form-input
                  id="prog-id"
                  v-model="form.programme_id"
                  placeholder="e.g. 10201"
                  :disabled="saving"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Degree" label-for="prog-degree">
                <b-form-select id="prog-degree" v-model="form.degree" :disabled="saving">
                  <option :value="null">-- Select --</option>
                  <option value="PGD">PGD</option>
                  <option value="MSc">MSc</option>
                  <option value="MA">MA</option>
                  <option value="MBA">MBA</option>
                  <option value="MPhil">MPhil</option>
                  <option value="PhD">PhD</option>
                  <option value="MED">MED</option>
                  <option value="MPH">MPH</option>
                  <option value="LLM">LLM</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Programme Type" label-for="prog-type">
                <b-form-select id="prog-type" v-model="form.prog_type" :disabled="saving">
                  <option :value="null">-- Select --</option>
                  <option value="FT">Full Time (FT)</option>
                  <option value="PT">Part Time (PT)</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Duration" label-for="prog-duration">
                <b-form-input
                  id="prog-duration"
                  v-model="form.duration"
                  placeholder="e.g. 2 years"
                  :disabled="saving"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Status" label-for="prog-status">
                <b-form-select id="prog-status" v-model="form.prog_status" :disabled="saving">
                  <option :value="null">-- Select --</option>
                  <option :value="1">Active</option>
                  <option :value="0">Inactive</option>
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </b-form>
      </b-modal>

      <!-- Bulk Update Modal -->
      <b-modal
        v-model="showBulkModal"
        title="Bulk Update Programmes"
        @ok.prevent="bulkUpdate"
        :ok-disabled="bulkSaving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="bulkSaving" class="mr-1"></b-spinner>
          {{ bulkSaving ? 'Updating...' : 'Update ' + selectedIds.length + ' Programme(s)' }}
        </template>
        <p class="text-muted small">Only fields you set below will be updated. Leave a field empty to keep its current value.</p>
        <b-form-group label="Department">
          <b-form-select v-model="bulkForm.department_id" :disabled="bulkSaving">
            <option :value="null">-- No change --</option>
            <option v-for="d in allDepartments" :key="d.id" :value="d.id">
              {{ d.department }} ({{ d.college ? d.college.college : '' }})
            </option>
          </b-form-select>
        </b-form-group>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Degree">
              <b-form-select v-model="bulkForm.degree" :disabled="bulkSaving">
                <option value="">-- No change --</option>
                <option value="PGD">PGD</option>
                <option value="MSc">MSc</option>
                <option value="MA">MA</option>
                <option value="MBA">MBA</option>
                <option value="MPhil">MPhil</option>
                <option value="PhD">PhD</option>
                <option value="MED">MED</option>
                <option value="MPH">MPH</option>
                <option value="LLM">LLM</option>
                <option value="MPHIL-PHD">MPHIL-PHD</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Programme Type">
              <b-form-select v-model="bulkForm.prog_type" :disabled="bulkSaving">
                <option value="">-- No change --</option>
                <option value="FT">Full Time (FT)</option>
                <option value="PT">Part Time (PT)</option>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Duration">
              <b-form-input v-model="bulkForm.duration" :disabled="bulkSaving" placeholder="-- No change --"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Status">
              <b-form-select v-model="bulkForm.prog_status" :disabled="bulkSaving">
                <option value="">-- No change --</option>
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      programmes: [],
      colleges: [],
      allDepartments: [],
      loading: false,
      saving: false,
      deleting: false,
      deletingId: null,
      selectedIds: [],
      showBulkModal: false,
      bulkSaving: false,
      bulkForm: { department_id: null, degree: "", prog_type: "", duration: "", prog_status: "" },
      filter: "",
      collegeFilter: "",
      departmentFilter: "",
      degreeFilter: "",
      progTypeFilter: "",
      perPage: 20,
      currentPage: 1,
      showModal: false,
      isEditing: false,
      editId: null,
      form: {
        code: "",
        programme: "",
        department_id: null,
        programme_id: "",
        degree: null,
        prog_type: null,
        duration: "",
        prog_status: null,
      },
      formErrors: {},
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "50px" } },
        { key: "code", label: "Code", sortable: true },
        { key: "programme", label: "Programme", sortable: true },
        { key: "department_name", label: "Department", sortable: true },
        { key: "degree", label: "Degree", sortable: true },
        { key: "prog_type", label: "Type", class: "text-center", sortable: true },
        { key: "duration", label: "Duration", class: "text-center" },
        { key: "prog_status", label: "Status", class: "text-center", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "120px" } },
      ],
    };
  },
  computed: {
    tableFields() {
      return [
        { key: "selected", label: "", class: "text-center", thStyle: { width: "40px" } },
        ...this.fields,
      ];
    },
    allSelected() {
      return this.filteredProgrammes.length > 0 && this.selectedIds.length === this.filteredProgrammes.length;
    },
    filteredDepartments() {
      if (!this.collegeFilter) return this.allDepartments;
      return this.allDepartments.filter((d) => d.college_id == this.collegeFilter);
    },
    degreeOptions() {
      const degrees = [...new Set(this.programmes.map((p) => p.degree).filter(Boolean))];
      return degrees.sort();
    },
    filteredProgrammes() {
      let list = this.programmes;
      if (this.degreeFilter) {
        list = list.filter((p) => p.degree === this.degreeFilter);
      }
      if (this.progTypeFilter) {
        list = list.filter((p) => p.prog_type === this.progTypeFilter);
      }
      return list;
    },
  },
  created() {
    this.loadColleges();
    this.loadAllDepartments();
    this.loadProgrammes();
  },
  methods: {
    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx > -1) this.selectedIds.splice(idx, 1);
      else this.selectedIds.push(id);
    },
    toggleSelectAll(checked) {
      this.selectedIds = checked ? this.filteredProgrammes.map((p) => p.id) : [];
    },
    openBulkUpdateModal() {
      this.bulkForm = { department_id: null, degree: "", prog_type: "", duration: "", prog_status: "" };
      this.showBulkModal = true;
    },
    bulkUpdate() {
      let payload = { ids: this.selectedIds };
      if (this.bulkForm.department_id) payload.department_id = this.bulkForm.department_id;
      if (this.bulkForm.degree) payload.degree = this.bulkForm.degree;
      if (this.bulkForm.prog_type) payload.prog_type = this.bulkForm.prog_type;
      if (this.bulkForm.duration) payload.duration = this.bulkForm.duration;
      if (this.bulkForm.prog_status) payload.prog_status = this.bulkForm.prog_status;

      if (Object.keys(payload).length <= 1) {
        Toast.fire({ icon: "warning", title: "Select at least one field to update" });
        return;
      }
      this.bulkSaving = true;
      axios.post("admin/programmes/bulk-update", payload)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.showBulkModal = false;
            this.selectedIds = [];
            this.loadProgrammes();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Update failed" });
          }
        })
        .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Bulk update failed" }))
        .finally(() => (this.bulkSaving = false));
    },
    loadColleges() {
      axios
        .get("admin/colleges")
        .then((res) => {
          if (res.data.msg === "success") this.colleges = res.data.data;
        })
        .catch(() => {});
    },
    loadAllDepartments() {
      axios
        .get("admin/departments")
        .then((res) => {
          if (res.data.msg === "success") this.allDepartments = res.data.data;
        })
        .catch(() => {});
    },
    loadProgrammes() {
      this.loading = true;
      this.selectedIds = [];
      let params = {};
      if (this.departmentFilter) params.department_id = this.departmentFilter;
      axios
        .get("admin/programmes", { params })
        .then((res) => {
          if (res.data.msg === "success") this.programmes = res.data.data;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load programmes" }))
        .finally(() => (this.loading = false));
    },
    onCollegeFilterChange() {
      this.departmentFilter = "";
      this.loadProgrammes();
    },
    openCreateModal() {
      this.isEditing = false;
      this.editId = null;
      this.form = {
        code: "",
        programme: "",
        department_id: null,
        programme_id: "",
        degree: null,
        prog_type: null,
        duration: "",
        prog_status: null,
      };
      this.formErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editId = item.id;
      this.form = {
        code: item.code || "",
        programme: item.programme || "",
        department_id: item.department_id,
        programme_id: item.programme_id || "",
        degree: item.degree || null,
        prog_type: item.prog_type || null,
        duration: item.duration || "",
        prog_status: item.prog_status != null ? Number(item.prog_status) : null,
      };
      this.formErrors = {};
      this.showModal = true;
    },
    saveProgramme() {
      this.formErrors = {};
      if (!this.form.programme.trim()) {
        this.formErrors.programme = "Programme name is required.";
        return;
      }
      if (!this.form.code.trim()) {
        this.formErrors.code = "Programme code is required.";
        return;
      }
      if (!this.form.department_id) {
        this.formErrors.department_id = "Please select a department.";
        return;
      }
      this.saving = true;
      let request;
      if (this.isEditing) {
        request = axios.put("admin/programmes/" + this.editId, this.form);
      } else {
        request = axios.post("admin/programmes", this.form);
      }
      request
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: this.isEditing ? "Programme updated" : "Programme created" });
            this.showModal = false;
            this.loadProgrammes();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            let data = err.response.data;
            if (data.errors) {
              let errs = {};
              Object.entries(data.errors).forEach(([k, v]) => (errs[k] = v.join(", ")));
              this.formErrors = errs;
            } else {
              Toast.fire({ icon: "error", title: data.message || "Validation error" });
            }
          } else {
            Toast.fire({ icon: "error", title: "An error occurred" });
          }
        })
        .finally(() => (this.saving = false));
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete Programme?",
        text: `Are you sure you want to delete "${item.programme}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleting = true;
          this.deletingId = item.id;
          axios
            .delete("admin/programmes/" + item.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Programme deleted" });
                this.loadProgrammes();
              } else {
                Toast.fire({ icon: "error", title: res.data.message || "Delete failed" });
              }
            })
            .catch((err) => {
              let msg = err.response?.data?.message || "Failed to delete programme";
              Toast.fire({ icon: "error", title: msg });
            })
            .finally(() => {
              this.deleting = false;
              this.deletingId = null;
            });
        }
      });
    },
  },
};
</script>
